import React, { useState, useMemo, useCallback, useRef } from "react";
import FormattedText from "../../../FormattedText/FormattedText";
import type {
  ComparePlansModalProps,
  ComparePlansSectionFilter,
  ComparePlansSectionBenefit,
  ComparePlansSectionFooterCta,
  ComparisonId,
} from "./types";
import {
  Overlay,
  Panel,
  HeaderWrapper,
  HeaderRow,
  BackButton,
  BackArrowSvg,
  HeaderTitle,
  FilterTabsRow,
  FilterTab,
  FilterTabText,
  TableAndFooterWrap,
  TableScrollArea,
  TableScrollContent,
  TableHeaderRow,
  TableHeaderCellFirst,
  TableHeaderCell,
  HeaderBenefitsLabel,
  HeaderPlanName,
  BadgeWrap,
  BadgeText,
  TableRow,
  TableCellFirst,
  TableCell,
  BenefitCellContent,
  BenefitIconWrap,
  BenefitIconImg,
  BenefitTextRow,
  BenefitLabel,
  CellValueIcon,
  CellValueText,
  TooltipTrigger,
  TooltipInfoIcon,
  TooltipOverlay,
  TooltipBubble,
  TooltipArrow,
  TooltipText,
  TooltipCloseBtn,
  FooterContainer,
  FooterRow,
  FooterCellFirst,
  FooterPriceLabel,
  FooterCell,
  FooterPriceWrap,
  FooterPrice,
  FooterUnit,
  BuyButton,
  BuyButtonText,
} from "./Styles";

const COMPARE_INFO_ICON_URI =
  "https://tripmoneycmsimgak.mmtcdn.com/img/Info_Outline_1cf24713d6.png";

/* ── Tooltip ── */

interface TooltipState {
  visible: boolean;
  text: string;
  top: number;
  left: number;
}

const TooltipPortal: React.FC<{
  tooltip: TooltipState;
  onClose: () => void;
}> = ({ tooltip, onClose }) => {
  if (!tooltip.visible) return null;
  return (
    <TooltipOverlay onClick={onClose}>
      <TooltipBubble
        style={{ top: tooltip.top, left: tooltip.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <TooltipArrow />
        <TooltipText>{tooltip.text}</TooltipText>
        <TooltipCloseBtn type="button" onClick={onClose}>
          ×
        </TooltipCloseBtn>
      </TooltipBubble>
    </TooltipOverlay>
  );
};

/* ── Filter tabs ── */

const FilterTabs: React.FC<{
  filters: ComparePlansSectionFilter[];
  activeFilter: string;
  onFilterChange: (v: string) => void;
}> = React.memo(({ filters, activeFilter, onFilterChange }) => (
  <FilterTabsRow>
    {filters.map((f) => (
      <FilterTab
        key={f.value}
        type="button"
        $active={activeFilter === f.value}
        onClick={() => onFilterChange(f.value)}
      >
        <FilterTabText $active={activeFilter === f.value}>
          {f.label}
        </FilterTabText>
      </FilterTab>
    ))}
  </FilterTabsRow>
));
FilterTabs.displayName = "FilterTabs";

/* ── Table header ── */

const CompareTableHeader: React.FC<{
  header: ComparePlansModalProps["data"]["header"];
  quoteIds: ComparisonId[];
  recommendedQuoteId: ComparisonId | null;
}> = React.memo(({ header, quoteIds, recommendedQuoteId }) => (
  <TableHeaderRow>
    <TableHeaderCellFirst>
      <HeaderBenefitsLabel>BENEFITS</HeaderBenefitsLabel>
    </TableHeaderCellFirst>
    {quoteIds.map((qid, idx) => {
      const item = header[String(qid)];
      const isLast = idx === quoteIds.length - 1;
      const isRec = qid === recommendedQuoteId;

      const gradientCss = item?.badge?.styles?.bgColorGradient?.length
        ? `linear-gradient(90deg, ${item.badge.styles.bgColorGradient.join(", ")})`
        : undefined;

      return (
        <TableHeaderCell
          key={String(qid)}
          $isLast={isLast}
          $recommended={isRec}
        >
          {item?.badge && (
            <BadgeWrap $gradient={gradientCss}>
              <BadgeText $color={item.badge.styles?.color}>
                {item.badge.text}
              </BadgeText>
            </BadgeWrap>
          )}
          <HeaderPlanName>
            <FormattedText value={item?.title} />
          </HeaderPlanName>
        </TableHeaderCell>
      );
    })}
  </TableHeaderRow>
));
CompareTableHeader.displayName = "CompareTableHeader";

/* ── Benefit tooltip trigger ── */

const BenefitTooltipTriggerBtn: React.FC<{
  onOpen: (rect: DOMRect) => void;
}> = ({ onOpen }) => {
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <TooltipTrigger
      ref={ref}
      type="button"
      onClick={() => {
        if (ref.current) onOpen(ref.current.getBoundingClientRect());
      }}
    >
      <TooltipInfoIcon src={COMPARE_INFO_ICON_URI} alt="info" />
    </TooltipTrigger>
  );
};

/* ── Table row ── */

const CompareTableRow: React.FC<{
  benefit: ComparePlansSectionBenefit;
  quoteIds: ComparisonId[];
  recommendedQuoteId: ComparisonId | null;
  rowIndex: number;
  onTooltipPress: (text: string, rect: DOMRect) => void;
}> = React.memo(({ benefit, quoteIds, recommendedQuoteId, rowIndex, onTooltipPress }) => {
  const { label, data } = benefit;
  const isAlternate = rowIndex % 2 === 1;

  return (
    <TableRow $alternate={isAlternate}>
      <TableCellFirst $alternate={isAlternate}>
        <BenefitCellContent>
          {label.icon && (
            <BenefitIconWrap>
              <BenefitIconImg src={label.icon} alt="" />
            </BenefitIconWrap>
          )}
          <BenefitTextRow>
            <BenefitLabel>{label.text}</BenefitLabel>
            {label.tooltip && (
              <BenefitTooltipTriggerBtn
                onOpen={(rect) => onTooltipPress(label.tooltip!, rect)}
              />
            )}
          </BenefitTextRow>
        </BenefitCellContent>
      </TableCellFirst>

      {quoteIds.map((qid, idx) => {
        const cellData = data?.[String(qid)];
        const isLast = idx === quoteIds.length - 1;
        const isRec = qid === recommendedQuoteId;

        let content: React.ReactNode = null;
        if (cellData?.icon !== undefined) {
          content = <CellValueIcon src={cellData.icon} alt="" />;
        } else if (cellData?.title) {
          content = (
            <CellValueText>
              <FormattedText value={cellData.title} />
            </CellValueText>
          );
        }

        return (
          <TableCell
            key={String(qid)}
            $isLast={isLast}
            $recommended={isRec && !isAlternate}
            $recommendedAlt={isRec && isAlternate}
          >
            {content}
          </TableCell>
        );
      })}
    </TableRow>
  );
});
CompareTableRow.displayName = "CompareTableRow";

/* ── Table footer ── */

const CompareTableFooter: React.FC<{
  footer: ComparePlansModalProps["data"]["footer"];
  quoteIds: ComparisonId[];
  recommendedQuoteId: ComparisonId | null;
  onBuyPress: (
    ctaParams: ComparePlansSectionFooterCta["params"],
    quoteId: string
  ) => void;
}> = React.memo(({ footer, quoteIds, recommendedQuoteId, onBuyPress }) => {
  const { label, data } = footer;

  return (
    <FooterContainer>
      <FooterRow>
        <FooterCellFirst>
          <FooterPriceLabel>
            <FormattedText value={label} />
          </FooterPriceLabel>
        </FooterCellFirst>

        {quoteIds.map((qid, idx) => {
          const item = data?.[String(qid)];
          const isLast = idx === quoteIds.length - 1;
          const isRec = qid === recommendedQuoteId;

          return (
            <FooterCell
              key={String(qid)}
              $isLast={isLast}
              $recommended={isRec}
            >
              <FooterPriceWrap>
                <FooterPrice>
                  <FormattedText value={item?.label} />
                </FooterPrice>
                <FooterUnit>
                  <FormattedText value={item?.unit} />
                </FooterUnit>
              </FooterPriceWrap>
              <BuyButton
                type="button"
                onClick={() =>
                  onBuyPress(item?.cta?.params ?? {}, String(qid))
                }
              >
                <BuyButtonText>
                  {item?.cta?.text || "BUY NOW"}
                </BuyButtonText>
              </BuyButton>
            </FooterCell>
          );
        })}
      </FooterRow>
    </FooterContainer>
  );
});
CompareTableFooter.displayName = "CompareTableFooter";

/* ── Main ── */

export const ComparePlansModal: React.FC<ComparePlansModalProps> = ({
  open,
  data,
  comparisonIds,
  listingParams,
  type,
  onClose,
  onBuyNow,
}) => {
  const { title, filters, header, footer, body } = data;

  const [activeFilter, setActiveFilter] = useState("");
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    text: "",
    top: 0,
    left: 0,
  });

  const quoteIds = useMemo(() => Array.from(comparisonIds), [comparisonIds]);

  const recommendedQuoteId = useMemo(() => {
    for (const qid of quoteIds) {
      if (header[String(qid)]?.badge) return qid;
    }
    return null;
  }, [header, quoteIds]);

  const handleFilterChange = useCallback((value: string) => {
    setActiveFilter((prev) => (prev === value ? "" : value));
  }, []);

  const filteredBenefits = useMemo(() => {
    if (!activeFilter || !filters?.length) return body;
    return body.filter(
      (b: ComparePlansSectionBenefit) =>
        b.filterType === activeFilter || b.type === activeFilter
    );
  }, [body, activeFilter, filters]);

  const handleTooltipPress = useCallback((text: string, rect: DOMRect) => {
    setTooltip({
      visible: true,
      text,
      top: rect.top + rect.height / 2 - 24,
      left: rect.right + 6,
    });
  }, []);

  const handleTooltipClose = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleBuyPress = useCallback(
    (
      ctaParams: ComparePlansSectionFooterCta["params"],
    ) => {
      if (onBuyNow) {
        onBuyNow({
          ...listingParams,
          ...ctaParams,
          type,
        });
      }
      onClose();
    },
    [onBuyNow, listingParams, type, onClose]
  );

  if (!open) return null;

  return (
    <Overlay role="presentation">
      <Panel role="dialog" aria-modal aria-labelledby="compare-title">
        {/* Header */}
        <HeaderWrapper>
          <HeaderRow>
            <BackButton type="button" onClick={onClose} aria-label="Back">
              <BackArrowSvg viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                  fill="#757575"
                />
              </BackArrowSvg>
            </BackButton>
            <HeaderTitle id="compare-title">{title}</HeaderTitle>
          </HeaderRow>

          {filters && filters.length > 0 && (
            <FilterTabs
              filters={filters}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />
          )}
        </HeaderWrapper>

        {/* Table + Footer */}
        <TableAndFooterWrap>
          <TableScrollArea>
            <TableScrollContent>
              <CompareTableHeader
                header={header}
                quoteIds={quoteIds}
                recommendedQuoteId={recommendedQuoteId}
              />
              {filteredBenefits.map(
                (benefit: ComparePlansSectionBenefit, index: number) => (
                  <CompareTableRow
                    key={`${benefit.label.text}-${index}`}
                    benefit={benefit}
                    quoteIds={quoteIds}
                    recommendedQuoteId={recommendedQuoteId}
                    rowIndex={index}
                    onTooltipPress={handleTooltipPress}
                  />
                )
              )}
            </TableScrollContent>
          </TableScrollArea>

          <CompareTableFooter
            footer={footer}
            quoteIds={quoteIds}
            recommendedQuoteId={recommendedQuoteId}
            onBuyPress={handleBuyPress}
          />
        </TableAndFooterWrap>

        {/* Tooltip */}
        <TooltipPortal tooltip={tooltip} onClose={handleTooltipClose} />
      </Panel>
    </Overlay>
  );
};
